import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ComplianceEvidencePack, ControlStatus, GapSeverity } from '@lizrd/contracts';
import { getComplianceEvidence } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Spinner } from '../components/ui/spinner';
import { typeLabel } from '../lib/graph-visuals';

const SEVERITY_BADGE: Record<GapSeverity, 'danger' | 'warning' | 'info'> = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
};

/** 0–100 → band + color, matching the Compliance page. */
function scoreMeta(score: number): { label: string; color: string } {
  if (score >= 80) return { label: 'strong', color: 'var(--success)' };
  if (score >= 50) return { label: 'needs attention', color: 'var(--warning)' };
  return { label: 'at risk', color: 'var(--danger)' };
}

function fmtDateTime(iso: string): string {
  // Deterministic, locale-stable rendering for an evidence artifact.
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function download(filename: string, text: string, mime: string): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Quote a CSV cell (RFC-4180-ish): wrap in quotes, double embedded quotes. */
const csv = (v: string | number | null | undefined): string => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** The asset register as long-format CSV (one row per resource × control) — the most
 * analysable artifact for an auditor's spreadsheet. */
function assetRegisterCsv(pack: ComplianceEvidencePack): string {
  const rows: string[] = [
    ['Resource', 'Type', 'Provider', 'Region', 'Control', 'NIS2 measure', 'Status'].join(','),
  ];
  for (const a of pack.assetRegister) {
    for (const c of a.controls) {
      rows.push(
        [
          csv(a.resourceName),
          csv(a.resourceType),
          csv(a.provider),
          csv(a.region),
          csv(c.label),
          csv(pack.policy.find((p) => p.controlId === c.controlId)?.nis2Measure ?? ''),
          csv(c.status),
        ].join(','),
      );
    }
  }
  return rows.join('\n');
}

function StatusChip({ status }: { status: ControlStatus }) {
  const map = {
    met: { label: 'Met', cls: 'bg-success/15 text-success' },
    gap: { label: 'Gap', cls: 'bg-danger/15 text-danger' },
    na: { label: 'n/a', cls: 'bg-surface-2 text-muted' },
  } as const;
  const m = map[status];
  return (
    <span className={`inline-block rounded-sm px-1.5 py-0.5 text-[0.6rem] font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
}

/** A section with a document-style heading. */
function Section({ n, title, children }: { n: string; title: string; children: ReactNode }) {
  return (
    <section className="evp-section break-inside-avoid">
      <h2 className="mb-2 flex items-baseline gap-2 border-b border-hairline pb-1 font-display text-sm font-bold uppercase tracking-wide text-bone">
        <span className="text-muted">{n}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

/**
 * The NIS2 evidence pack (G6) — a timestamped, data-resident document: scope,
 * posture, the enforced policy, ranked gaps, and a per-resource asset register, with
 * an honest "not a certification" disclaimer. Rendered chrome-free and print-optimized
 * (Print / Save-as-PDF), with JSON + CSV export. The board-oversight artifact, the
 * auditor pack, and the supplier-questionnaire answer set in one place.
 */
export function ComplianceEvidencePage() {
  const { domainId = '' } = useParams();
  const pack = useQuery({
    queryKey: ['compliance-evidence', domainId],
    queryFn: () => getComplianceEvidence(domainId),
  });

  if (pack.isLoading) {
    return (
      <div className="grid min-h-screen place-items-center bg-canvas text-muted">
        <Spinner />
      </div>
    );
  }
  if (pack.isError || !pack.data) {
    return (
      <div className="grid min-h-screen place-items-center bg-canvas">
        <div className="text-center">
          <p className="text-danger">Could not load the evidence pack.</p>
          <Link
            to={`/domains/${domainId}/compliance`}
            className="mt-2 inline-block text-sm font-semibold text-iris hover:text-iris-strong"
          >
            ← Back to NIS2 Compliance
          </Link>
        </div>
      </div>
    );
  }

  const p = pack.data;
  const m = p.metadata;
  const band = scoreMeta(p.posture.score);
  const stamp = m.generatedAt.replace(/[:.]/g, '-').slice(0, 19);

  return (
    <div className="min-h-screen bg-canvas text-haze">
      {/* Print rules: hide the toolbar, go white, avoid breaking rows across pages. */}
      <style>{`
        @media print {
          .evp-toolbar { display: none !important; }
          .evp-doc { max-width: none !important; margin: 0 !important; padding: 0 !important; }
          .break-inside-avoid { break-inside: avoid; }
          .evp-section { page-break-inside: auto; }
          tr { page-break-inside: avoid; }
        }
      `}</style>

      {/* Toolbar (screen only) */}
      <div className="evp-toolbar sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-b border-hairline bg-surface/95 px-4 py-2 backdrop-blur">
        <Link
          to={`/domains/${domainId}/compliance`}
          className="text-sm font-semibold text-iris hover:text-iris-strong"
        >
          ← Back to NIS2 Compliance
        </Link>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              download(
                `nis2-evidence-${m.domainName}-${stamp}.json`,
                JSON.stringify(p, null, 2),
                'application/json',
              )
            }
          >
            Download JSON
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              download(
                `nis2-asset-register-${m.domainName}-${stamp}.csv`,
                assetRegisterCsv(p),
                'text/csv',
              )
            }
          >
            Download CSV
          </Button>
          <Button size="sm" onClick={() => window.print()}>
            Print / Save as PDF
          </Button>
        </div>
      </div>

      {/* Document */}
      <div className="evp-doc mx-auto max-w-4xl px-6 py-8">
        {/* Masthead */}
        <header className="break-inside-avoid border-b-2 border-bone/20 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-display text-[0.7rem] font-bold uppercase tracking-[0.2em] text-iris">
                Lizrd · NIS2 Cloud Compliance
              </div>
              <h1 className="mt-1 font-display text-2xl font-bold text-bone">Evidence Pack</h1>
              <div className="mt-1 text-sm text-muted">
                {m.organizationName} · {m.domainName}
              </div>
            </div>
            <Badge variant="info">Data region: {m.region}</Badge>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-muted sm:grid-cols-4">
            <div>
              <div className="font-semibold uppercase tracking-wide text-faint">Generated</div>
              <div className="text-haze">{fmtDateTime(m.generatedAt)}</div>
            </div>
            <div>
              <div className="font-semibold uppercase tracking-wide text-faint">Framework</div>
              <div className="text-haze">{m.framework.toUpperCase()}</div>
            </div>
            <div>
              <div className="font-semibold uppercase tracking-wide text-faint">
                Assets in scope
              </div>
              <div className="text-haze">{p.posture.assetsInScope}</div>
            </div>
            <div>
              <div className="font-semibold uppercase tracking-wide text-faint">Generated by</div>
              <div className="truncate text-haze">{m.generatedByEmail ?? '—'}</div>
            </div>
          </div>
          <div className="mt-2 text-[0.7rem] text-faint">{m.frameworkReference}</div>
        </header>

        {/* Disclaimer */}
        <div className="mt-4 break-inside-avoid rounded-sm border border-hairline bg-surface-2/50 p-3 text-xs leading-relaxed text-muted">
          <span className="font-semibold text-haze">Scope &amp; disclaimer. </span>
          {m.disclaimer}
        </div>

        <div className="mt-6 space-y-6">
          {/* 1 · Posture */}
          <Section n="1" title="Posture summary">
            <div className="flex flex-wrap items-end gap-6">
              <div>
                <div className="font-display text-5xl font-bold" style={{ color: band.color }}>
                  {p.posture.score}
                  <span className="text-xl text-muted">/100</span>
                </div>
                <div
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: band.color }}
                >
                  {band.label}
                </div>
              </div>
              <div className="flex gap-6 text-sm">
                <div>
                  <div className="font-display text-xl font-bold text-bone">
                    {p.posture.gapCount}
                  </div>
                  <div className="text-xs text-muted">open gaps</div>
                </div>
                <div>
                  <div className="font-display text-xl font-bold text-danger">
                    {p.posture.criticalGapCount}
                  </div>
                  <div className="text-xs text-muted">critical</div>
                </div>
                <div>
                  <div className="font-display text-xl font-bold text-bone">
                    {p.posture.controls.length}
                  </div>
                  <div className="text-xs text-muted">controls in force</div>
                </div>
              </div>
            </div>

            <table className="mt-4 w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-hairline text-left text-muted">
                  <th className="py-1 pr-2 font-semibold">Control</th>
                  <th className="py-1 pr-2 font-semibold">NIS2 measure</th>
                  <th className="py-1 pr-2 text-right font-semibold">Met</th>
                  <th className="py-1 pr-2 text-right font-semibold">Gaps</th>
                  <th className="py-1 pr-2 text-right font-semibold">Score</th>
                  <th className="py-1 font-semibold">Enforcement</th>
                </tr>
              </thead>
              <tbody>
                {p.posture.controls.map((c) => (
                  <tr key={c.controlId} className="border-b border-hairline/50">
                    <td className="py-1 pr-2 font-medium text-bone">{c.label}</td>
                    <td className="py-1 pr-2 text-muted">{c.nis2Measure}</td>
                    <td className="py-1 pr-2 text-right text-success">{c.met}</td>
                    <td className="py-1 pr-2 text-right text-danger">{c.gaps || '—'}</td>
                    <td className="py-1 pr-2 text-right font-semibold text-haze">{c.score}%</td>
                    <td className="py-1 text-muted">{c.gate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* 2 · Open gaps */}
          <Section n="2" title={`Open gaps (${p.gaps.length})`}>
            {p.gaps.length === 0 ? (
              <p className="text-sm text-success">
                No open gaps against the controls in force. Every in-scope resource meets the
                enforced NIS2 technical measures.
              </p>
            ) : (
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="border-b border-hairline text-left text-muted">
                    <th className="py-1 pr-2 font-semibold">Severity</th>
                    <th className="py-1 pr-2 font-semibold">Control</th>
                    <th className="py-1 pr-2 font-semibold">Resource</th>
                    <th className="py-1 pr-2 font-semibold">Finding</th>
                    <th className="py-1 font-semibold">Remediation</th>
                  </tr>
                </thead>
                <tbody>
                  {p.gaps.map((g) => (
                    <tr key={g.id} className="border-b border-hairline/50 align-top">
                      <td className="py-1.5 pr-2">
                        <Badge variant={SEVERITY_BADGE[g.severity]}>{g.severity}</Badge>
                      </td>
                      <td className="py-1.5 pr-2">
                        <div className="font-medium text-bone">{g.controlLabel}</div>
                        <div className="text-[0.6rem] text-muted">{g.nis2Measure}</div>
                      </td>
                      <td className="py-1.5 pr-2">
                        <div className="font-medium text-haze">{g.resourceName}</div>
                        <div className="text-[0.6rem] text-muted">
                          {typeLabel(g.resourceType, g.provider)}
                          {g.region ? ` · ${g.region}` : ''}
                        </div>
                      </td>
                      <td className="py-1.5 pr-2 text-muted">{g.summary}</td>
                      <td className="py-1.5 text-muted">{g.remediation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* 3 · Asset register */}
          <Section n="3" title={`Asset register (${p.assetRegister.length} in scope)`}>
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-hairline text-left text-muted">
                  <th className="py-1 pr-2 font-semibold">Resource</th>
                  <th className="py-1 pr-2 font-semibold">Type</th>
                  <th className="py-1 pr-2 font-semibold">Region</th>
                  <th className="py-1 font-semibold">Controls (met / gap)</th>
                </tr>
              </thead>
              <tbody>
                {p.assetRegister.map((a) => (
                  <tr key={a.nodeExternalId} className="border-b border-hairline/50 align-top">
                    <td className="py-1.5 pr-2 font-medium text-bone">{a.resourceName}</td>
                    <td className="py-1.5 pr-2 text-muted">
                      {typeLabel(a.resourceType, a.provider)}
                    </td>
                    <td className="py-1.5 pr-2 text-muted">{a.region ?? '—'}</td>
                    <td className="py-1.5">
                      <div className="flex flex-wrap items-center gap-1">
                        {a.controls.map((c) => (
                          <span key={c.controlId} className="inline-flex items-center gap-1">
                            <StatusChip status={c.status} />
                            <span className="text-[0.6rem] text-muted">{c.label}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </div>

        {/* Footer */}
        <footer className="mt-8 border-t border-hairline pt-3 text-[0.65rem] text-faint">
          Generated by Lizrd · {fmtDateTime(m.generatedAt)} · data region {m.region} · posture
          computed live from read-only cloud metadata. {m.frameworkReference}.
        </footer>
      </div>
    </div>
  );
}
